import React, { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import FormLabel from '@mui/material/FormLabel';
import TextField from '@mui/material/TextField';
import { useApi } from '../../../../../hooks/useApi';
import { useCompanyDispatch } from '../../../../../stateProviders/CompanyStateProvider';
import { AdditionalCost } from '../../../../../types/api/AdditionalCost';
import { ActionButtons } from '../../../../../globalComponents/modal/ActionButtons';
import { Modal } from '../../../../../globalComponents/modal/Modal';

export const EditAdditionalCostModal: React.FC<{
    companyId: string;
    rowData: AdditionalCost;
    setSelectedRow: (row: AdditionalCost) => void;
    gridRef: React.MutableRefObject<any>;
    modalOpen: boolean;
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ companyId, rowData, setSelectedRow, gridRef, modalOpen, setModalOpen }) => {
    const api = useApi();
    const companyDispatch = useCompanyDispatch();

    const [cost, setCost] = useState({
        costName: '',
        costDescription: '',
        costAmount: '',
    });

    useEffect(() => {
        setCost({
            costName: rowData.costName || '',
            costDescription: rowData.costDescription || '',
            costAmount: String(rowData.costAmount || ''),
        });
    }, [rowData]);

    return (
        <Modal
            key={rowData._id}
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            modalTitle={
                <Typography variant="h2" fontWeight="bold">
                    Edit Additional Cost
                </Typography>
            }
            modalBody={
                <Stack spacing={2} sx={{ mb: 2 }}>
                    {[{
                        label: 'Cost Name',
                        name: 'costName',
                        placeholder: 'Example: Admin Fees',
                        type: 'text',
                        required: true,
                        value: cost.costName,
                    }, {
                        label: 'Cost Amount ($)',
                        name: 'costAmount',
                        placeholder: '',
                        type: 'number',
                        required: true,
                        value: cost.costAmount,
                    }, {
                        label: 'Cost Description',
                        name: 'costDescription',
                        placeholder: 'Example: Estimated misc admin fees',
                        type: 'text',
                        required: false,
                        value: cost.costDescription,
                    }].map((field) => (
                        <Stack key={field.name}>
                            <FormLabel component="legend" sx={{ mb: 1 }} required={field.required}>
                                {field.label}
                            </FormLabel>
                            <TextField
                                fullWidth
                                name={field.name}
                                type={field.type}
                                placeholder={field.placeholder}
                                value={field.value}
                                slotProps={field.type === 'number' ? {
                                    htmlInput: { min: 0, step: 0.01 }
                                } : undefined}
                                onChange={(e) => {
                                    let value = e.target.value;
                                    if (field.type === 'number' && value === '') {
                                        value = '';
                                    }
                                    setCost({
                                        ...cost,
                                        [field.name]: value,
                                    });
                                }}
                            />
                        </Stack>
                    ))}
                </Stack>
            }
            modalFooter={
                <ActionButtons
                    confirmButtonText="Edit"
                    confirmCallback={async () => {
                        await api.additionalCost.update(rowData._id, {
                            costName: cost.costName.trim(),
                            costAmount: Number(cost.costAmount) || 0,
                            costDescription: cost.costDescription.trim()
                        });
                        const additionalCosts = await api.additionalCost.search({ companyId });
                        companyDispatch({ additionalCosts });
                        gridRef.current?.onRefresh();
                        setModalOpen(false);
                    }}
                    confirmDisabled={
                        cost.costName.trim() === '' ||
                        cost.costAmount === '' ||
                        Number(cost.costAmount) <= 0 ||
                        isNaN(Number(cost.costAmount))
                    }
                    confirmButtonStyle="success"
                    cancelCallback={() => setModalOpen(false)}
                />
            }
        />
    );
}